import { type User, editableVenueIsCreatable } from '@shady-pines/db';
import { now, strongFromEntries, strongKeys, type Uuid } from '@shady-pines/types';
import { isEmpty, isNil } from 'lodash';
import { supabaseApi } from '../api/api-store';
import type { EventState } from './types';
export type EventVenueApi = ReturnType<typeof EventVenueApi>;

export const EventVenueApi = (
  get: () => EventState,
  set: (partial: Partial<EventState> | ((state: EventState) => Partial<EventState>)) => void,
) => ({
  createVenue: async (me: Uuid<User>) => {
    const loading = get().loading.createVenue;

    if (loading) {
      return;
    }

    const editableVenue = get().editableVenue;
    if (!editableVenueIsCreatable(editableVenue)) {
      return null;
    }

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            createVenue: true,
          },
        }) as const satisfies Partial<EventState>,
    );

    const { data: newVenue } = await supabaseApi()
      .from('event_venues')
      .insert({ ...editableVenue, created_by: me })
      .select('*')
      .single();

    if (isNil(newVenue)) {
      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              createVenue: false,
            },
          }) as const satisfies Partial<EventState>,
      );
      return null;
    }

    // should not be possible for venues to be nil at this point, if everything is set up correctly
    // this function is unreachable bc the venues should already have been loaded
    set(
      (s) =>
        ({
          eventVenues: isNil(s.eventVenues)
            ? null
            : {
                ...s.eventVenues,
                [newVenue.id]: newVenue,
              },
          loading: {
            ...s.loading,
            createVenue: false,
          },
        }) as const satisfies Partial<EventState>,
    );

    return newVenue.id;
  },

  fetchEventVenues: async () => {
    const loading = get().loading.venues;

    if (loading) {
      return;
    }

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            venues: true,
          },
        }) as const satisfies Partial<EventState>,
    );

    const venues = await supabaseApi().from('event_venues').select('*').is('deleted_at', null);

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            venues: false,
          },
          eventVenues: strongFromEntries((venues.data ?? []).map((v) => [v.id, v])),
          lastEventVenueRefresh: now(),
        }) as const satisfies Partial<EventState>,
    );
  },

  refreshEventVenues: async () => {
    const loading = get().loading.venues;

    if (loading) {
      return;
    }

    set(
      (s) =>
        ({
          loading: { ...s.loading, venues: true },
        }) as const satisfies Partial<EventState>,
    );

    const lastEventVenueRefresh = get().lastEventVenueRefresh;
    const venueIds = strongKeys(get().eventVenues ?? {});

    // if the show count has changed, it means that a show has been activated or deactivated and we should just refetch everything
    const { count } = await supabaseApi()
      .from('event_venues')
      .select('*', { count: 'exact', head: true })
      .in('id', venueIds);

    if (count !== venueIds.length) {
      set(
        (s) =>
          ({
            loading: { ...s.loading, venues: false },
            lastEventRefresh: now(),
          }) as const satisfies Partial<EventState>,
      );
      return {
        result: 'full-refresh-needed',
      } as const;
    }

    const { data: updatedVenues } = await supabaseApi()
      .from('event_venues')
      .select('*')
      .is('deleted_at', null)
      .gt('updated_at', lastEventVenueRefresh ?? now());

    if (isNil(updatedVenues) || isEmpty(updatedVenues)) {
      set(
        (s) =>
          ({
            loading: { ...s.loading, venues: false },
            lastEventVenueRefresh: now(),
          }) as const satisfies Partial<EventState>,
      );
      return {
        result: 'no-refresh-required',
      } as const;
    }

    set((s) => {
      return {
        loading: { ...s.loading, venues: false },
        eventVenues: {
          ...s.eventVenues!,
          ...strongFromEntries(updatedVenues.map((u) => [u.id, u])),
        },
        lastEventVenueRefresh: now(),
      } as const satisfies Partial<EventState>;
    });

    return {
      result: 'refreshed',
      refreshedIds: updatedVenues.map((u) => u.id),
    } as const;
  },
});
